import React, { createContext, useCallback, useMemo, useState } from "react";
import { ConsentState, loadStoredConsent, saveConsent, initGA } from "@/lib/analytics";

export interface ConsentContextValue {
  consent: ConsentState | null;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updateConsent: (updates: Partial<Omit<ConsentState, "essential" | "timestamp">>) => void;
  dismissBanner: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ConsentContext = createContext<ConsentContextValue | null>(null);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lazy initializers run once — also safe place to bootstrap GA for returning visitors
  const [consent, setConsent] = useState<ConsentState | null>(() => {
    const stored = loadStoredConsent();
    if (stored) {
      initGA(stored);
    }
    return stored;
  });
  const [showBanner, setShowBanner] = useState<boolean>(() => !loadStoredConsent());

  const applyConsent = useCallback((next: ConsentState) => {
    saveConsent(next);
    setConsent(next);
    initGA(next);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    applyConsent({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: Date.now(),
    });
  }, [applyConsent]);

  const rejectAll = useCallback(() => {
    applyConsent({
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: Date.now(),
    });
  }, [applyConsent]);

  const updateConsent = useCallback(
    (updates: Partial<Omit<ConsentState, "essential" | "timestamp">>) => {
      const base = consent ?? {
        essential: true as const,
        analytics: false,
        marketing: false,
        functional: false,
      };
      applyConsent({
        ...base,
        ...updates,
        essential: true,
        timestamp: Date.now(),
      });
    },
    [consent, applyConsent]
  );

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  // Memoized so consumers only redraw when consent state actually changes.
  const value = useMemo(
    () => ({ consent, showBanner, acceptAll, rejectAll, updateConsent, dismissBanner }),
    [consent, showBanner, acceptAll, rejectAll, updateConsent, dismissBanner]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};
